import React from 'react';
import Modal from '@common/components/Modal';
import SpinnerContainer from '@common/components/SpinnerContainer';
import userPersonas from '@common/constants/userPersonas';
import PaymentPlanInstallmentRequestPage from './modules/InstallmentRequestPage';
import PaymentPlanInstallmentRequestSentPage from './modules/InstallmentRequestSentPage';
import { DEBOUNCE_WAIT_TIME } from '@common/constants';
import { debounce } from '@zg-rentals/zdash';

const DEFAULT_TENANT_INDEX = 0;

type Props = {
  rentTermsKeyArray: string[];
  tenantNameArray: string[];
  onSubmit: (args: any) => Record<string, any>;
  onClose: (paymentPlanRequested, tenantName, requestKeysCollection) => Promise<any>;
  paymentPlanFeaturePermissions: Record<string, any>;
  paymentPlanInstallmentNumber: Record<string, number>;
};

const PaymentPlanInstallmentRequests: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  const initialPaymentPlanDetails = {
    amount: '',
    notes: '',
    tenantName: '',
  };

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showRequestSentScreen, setShowRequestSentScreen] = React.useState<boolean>(false);
  const [selectedTenantIndex, setSelectedTenantIndex] = React.useState<number>(DEFAULT_TENANT_INDEX);
  const [paymentPlanDetails, setPaymentPlanDetails] = React.useState<Record<string, any>>(initialPaymentPlanDetails);
  const [paymentPlanNumber, setPaymentPlanNumber] = React.useState<Record<string, number>>(
    props.paymentPlanInstallmentNumber,
  );
  const [paymentPlanRequested, setPaymentPlanRequested] = React.useState<boolean>(false);
  const [requestKeysCollection, setRequestKeysCollection] = React.useState<string[]>([]);

  const handleTenantChange = (index: number): void => {
    setSelectedTenantIndex(index);
  };

  const goToInstallmentRequestPage = () => {
    setShowRequestSentScreen(false);
    setPaymentPlanNumber({
      ...paymentPlanNumber,
      [props.rentTermsKeyArray[selectedTenantIndex]]:
        paymentPlanNumber[props.rentTermsKeyArray[selectedTenantIndex]] + 1,
    });
  };

  const onSubmit = async (payload): Promise<void> => {
    const { rentTermsKey, amount, type, notes, tenantName } = payload;
    setIsLoading(true);
    const response = await props.onSubmit({ rentTermsKey, amount, type, notes });
    setIsLoading(false);

    if (response.error) {
      return;
    }

    setRequestKeysCollection([...requestKeysCollection, ...response.requestKeys]);

    setPaymentPlanDetails({
      amount,
      notes,
      tenantName,
      paymentPlanNumber: paymentPlanNumber[props.rentTermsKeyArray[selectedTenantIndex]],
    });

    setShowRequestSentScreen(true);
    setPaymentPlanRequested(true);
  };

  const onClose = (): void => {
    const debouncedResend: any = debounce(props.onClose, DEBOUNCE_WAIT_TIME, true);
    return debouncedResend(paymentPlanRequested, paymentPlanDetails.tenantName, requestKeysCollection);
  };
  return (
    <Modal
      persona={userPersonas.LANDLORD}
      header={showRequestSentScreen ? 'Request sent' : 'Payment request'}
      onRequestClose={onClose}
      dataAutoTestId="landlord-payment-plan-installment-requests"
    >
      {isLoading && <SpinnerContainer />}
      {showRequestSentScreen ? (
        <PaymentPlanInstallmentRequestSentPage
          onClose={onClose}
          paymentPlanDetails={paymentPlanDetails}
          goToInstallmentRequestPage={goToInstallmentRequestPage}
        />
      ) : (
        <PaymentPlanInstallmentRequestPage
          selectedTenantIndex={selectedTenantIndex}
          handleTenantChange={handleTenantChange}
          rentTermsKeyArray={props.rentTermsKeyArray}
          tenantNameArray={props.tenantNameArray}
          onSubmit={onSubmit}
          onClose={onClose}
          paymentPlanFeaturePermissions={props.paymentPlanFeaturePermissions}
          paymentPlanNumber={paymentPlanNumber[props.rentTermsKeyArray[selectedTenantIndex]]}
        />
      )}
    </Modal>
  );
};

export default PaymentPlanInstallmentRequests;
